import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { getInvoicePrefs, matchesSubscriber, setInvoicePref, switchesByAccount, type InvoicePrefSubscriber } from "../../api/invoicePrefs";
import { getPinOffer } from "../../api/pinPlans";
import { shortName } from "../../auth/scope";
import { SubscriberReceiptsDialog } from "./SubscriberReceiptsDialog";

/**
 * Users › Subscriber profiles (owner, 2026-09-19): a PIN operator opens a
 * subscriber's Edit profile and turns on "Issue tax invoice" for the clients
 * who want one. Only those get a tax invoice, and it is issued in the
 * upstream (parent) operator's name. Everyone else's supply is still recorded.
 *
 * radaccess says whether the switch applies (operator type PIN); for any other
 * operator this screen says so and edits nothing.
 *
 * Every operator can open a subscriber's Receipts and send one to that
 * subscriber when they ask (owner 2026-10-10).
 */
export function SubscriberProfilesScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const qc = useQueryClient();
  const enabled = Boolean(identity.token && ns);

  const prefs = useQuery({ queryKey: ["invoiceprefs", ns], enabled, queryFn: ({ signal }) => getInvoicePrefs(identity, ns, signal), retry: false });
  const offer = useQuery({ queryKey: ["pinoffer", ns], enabled, queryFn: ({ signal }) => getPinOffer(identity, ns, signal), retry: false });

  const on = switchesByAccount(prefs.data?.prefs ?? []);
  const applies = prefs.data?.applies ?? false;
  const upstream = offer.data?.parent ? shortName(offer.data.parent) : "your upstream provider";

  const [q, setQ] = useState("");
  const rows = (prefs.data?.subscribers ?? []).filter((a) => matchesSubscriber(a, q));

  const [editing, setEditing] = useState<InvoicePrefSubscriber | null>(null);
  const [receiptsFor, setReceiptsFor] = useState<InvoicePrefSubscriber | null>(null);
  const [draft, setDraft] = useState(false);
  const save = useMutation({
    mutationFn: (v: { account: InvoicePrefSubscriber; on: boolean }) => setInvoicePref(identity, ns, v.account.accountid, v.on),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["invoiceprefs", ns] });
      setEditing(null);
    },
  });

  const err = prefs.error;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="h5" component="h1">
          Subscriber profiles
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose which subscribers receive a tax invoice, and send a subscriber the receipt for a payment when they ask.
        </Typography>
      </Box>
      {err && <Alert severity="error">{err instanceof Error ? err.message : String(err)}</Alert>}
      {prefs.data && !applies && (
        <Alert severity="info">Only a PIN operator chooses which subscribers get a tax invoice. Your subscribers are invoiced as before.</Alert>
      )}
      {prefs.data && <TextField size="small" label="Search mobile, name or account" autoFocus inputProps={{ inputMode: "search" }} value={q} onChange={(e) => setQ(e.target.value)} sx={{ maxWidth: 360 }} />}
      {prefs.data && !prefs.isLoading && rows.length === 0 && <Alert severity="info">No subscribers yet.</Alert>}
      {rows.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Subscriber</TableCell>
                <TableCell>Tax invoice</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((a) => {
                const isOn = on.get(a.accountid.toLowerCase()) ?? false;
                return (
                  <TableRow key={a.accountid}>
                    <TableCell>
                      {a.name || a.username || <code>{a.accountid}</code>}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {[a.mobile, a.username].filter(Boolean).join(" · ")}
                      </Typography>
                    </TableCell>
                    <TableCell>{isOn ? <Chip size="small" color="success" label="Issued" /> : <Chip size="small" label="Not issued" />}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => setReceiptsFor(a)}>
                        Receipts
                      </Button>
                      <Button
                        size="small"
                        disabled={!applies}
                        onClick={() => {
                          save.reset();
                          setDraft(isOn);
                          setEditing(a);
                        }}
                      >
                        Edit profile
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <SubscriberReceiptsDialog ns={ns} subscriber={receiptsFor} onClose={() => setReceiptsFor(null)} />

      <Dialog open={editing !== null} onClose={() => setEditing(null)} fullWidth maxWidth="xs">
        <DialogTitle>Edit profile</DialogTitle>
        {editing && (
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography variant="body2">{editing.name || editing.username || editing.accountid}</Typography>
            <FormControlLabel control={<Switch checked={draft} onChange={(e) => setDraft(e.target.checked)} />} label="Issue tax invoice" />
            <Typography variant="caption" color="text.secondary">
              On: this subscriber&apos;s recharges are invoiced in {upstream}&apos;s name. Off: no tax invoice; the sale is still recorded.
            </Typography>
            {save.error && <Alert severity="error">{save.error instanceof Error ? save.error.message : String(save.error)}</Alert>}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" disabled={save.isPending} onClick={() => editing && save.mutate({ account: editing, on: draft })}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
